import { TEXT_MESSAGES_PRIVACY_ANCHOR } from '@supply-mover-2-0/shared';
import { useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX, ReactNode } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStatusBarSurface } from '../hooks/useStatusBarSurface';
import { telHref } from '../lib/supportContact';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * The date the wording below last changed. Bump it whenever the text changes;
 * carriers and customers read it as the version of these terms.
 */
const LAST_UPDATED = 'October 15, 2026';

/**
 * PD-17137 — the storefront's texting terms and privacy, under the brand's own
 * name, at /text-messages. The texting disclosure links here unless the brand
 * publishes its own policies (portal site settings). Each storefront has one,
 * so a brand without a website still has terms to show carriers and
 * customers, and a subsidiary brand never names its parent company.
 *
 * The wording follows the texting sections HF Foods' counsel approved for
 * hffoodsgroup.com (2026-10-14), with the brand's name in place of the
 * company's. It deliberately names no service provider.
 *
 * Public and chrome-less like AccountDeletedPage, outside PortalLayout, so it
 * is readable on a storefront that requires signing in to browse.
 */
export function TextMessagesPage(): JSX.Element {
	const { t } = useTranslation();
	const brand = usePortalSiteStore((s) => s.site?.displayName ?? '');
	const logoUrl = usePortalSiteStore((s) => s.site?.logoUrl ?? null);
	const logoUrlLight = usePortalSiteStore((s) => s.site?.logoUrlLight ?? null);
	const supportContact = usePortalSiteStore(
		(s) => s.site?.supportContact ?? null,
	);
	useStatusBarSurface('page');

	// The privacy link lands on the second part of this page; a hash on a
	// client-rendered page needs a nudge once the content exists.
	useEffect(() => {
		if (window.location.hash === `#${TEXT_MESSAGES_PRIVACY_ANCHOR}`) {
			document
				.getElementById(TEXT_MESSAGES_PRIVACY_ANCHOR)
				?.scrollIntoView();
		}
	}, []);

	// The card is the storefront's own surface, light or dark with the theme,
	// so the logo follows the theme like the header's does: the dark-ink
	// variant on a light card, the white one in dark mode. (The sign-in photo
	// theme, loginTheme, describes the sign-in screen only.)
	const { isDark } = useTheme();
	const logo = isDark ? logoUrl : (logoUrlLight ?? logoUrl);
	const pageCls = 'bg-brand-page-bg';
	const cardCls = 'bg-brand-surface border-brand-border';
	const bodyCls = 'text-brand-muted';

	const withBrand = (text: string): string => t(text).replace(/\{brand\}/g, t(brand));
	const supportEmail = supportContact?.email ?? null;
	const supportPhone = supportContact?.phone ?? null;
	const hasEmail = supportEmail != null && supportEmail !== '';
	const hasPhone = supportPhone != null && supportPhone !== '';

	const section = (title: string, body: ReactNode): JSX.Element => (
		<p className={`text-sm ${bodyCls}`}>
			<span className="font-semibold text-default">{t(title)}</span> {body}
		</p>
	);

	return (
		<div className={`min-h-dvh flex justify-center px-4 py-8 ${pageCls}`}>
			<div
				className={`w-full max-w-2xl border rounded-xl p-6 space-y-4 self-start ${cardCls}`}
			>
				{logo != null && (
					<img src={logo} alt={t(brand)} className="h-12 w-auto object-contain" />
				)}
				<h1 className="text-2xl font-semibold text-default">
					{t('Text Messages')}
				</h1>
				<p className={`text-xs ${bodyCls}`}>
					{t('Last updated {date}').replace('{date}', t(LAST_UPDATED))}
				</p>
				<p className={`text-sm ${bodyCls}`}>
					{withBrand(
						'These terms apply when you agree to receive text messages (SMS or MMS) from {brand}, or when you text one of our numbers.',
					)}
				</p>

				{section(
					'What we send.',
					t(
						'One-time sign-in codes for your online ordering account or app; account notifications, such as a statement being ready or an update on a support request; order confirmations and delivery updates; and conversations with our sales and customer service teams about your account and orders.',
					),
				)}
				{section(
					'How you agree.',
					t(
						'We text you only after you agree to receive texts, or to reply when you text us first. You can agree by requesting a sign-in code with your mobile number, by choosing text notifications in your ordering account, by giving us your mobile number when we set up your ordering login, or by asking a member of our team to text you. Consent is not a condition of any purchase.',
					),
				)}
				{section(
					'How often.',
					t(
						'Message frequency varies with your account activity and orders. Sign-in codes are sent only when you request one.',
					),
				)}
				{section(
					'Cost.',
					t('Message and data rates may apply. We do not charge for text messages.'),
				)}
				{section(
					'Stopping texts.',
					t(
						'Reply STOP to any message to stop texts from that number. We also honor END, CANCEL, UNSUBSCRIBE, QUIT, STOPALL, REVOKE and OPTOUT. You will receive one message confirming it, and no more texts unless you opt in again. If you stop sign-in codes, you will need to sign in another way.',
					),
				)}
				{section(
					'Help.',
					<>
						{t('Reply HELP to any message')}
						{hasEmail || hasPhone ? <>{t(', or contact us at')} </> : null}
						{hasEmail && (
							<a href={`mailto:${supportEmail}`} className="text-portal-accent hover:underline">
								{t(supportEmail)}
							</a>
						)}
						{hasEmail && hasPhone && <> {t('or')} </>}
						{hasPhone && (
							<a href={telHref(supportPhone)} className="text-portal-accent hover:underline">
								{t(supportPhone)}
							</a>
						)}
						{t('.')}
					</>,
				)}

				<h2
					id={TEXT_MESSAGES_PRIVACY_ANCHOR}
					className="pt-2 text-lg font-semibold text-default scroll-mt-4"
				>
					{t('Privacy')}
				</h2>
				{section(
					'Your information.',
					t(
						'We keep your mobile number, your name and business name if you give them to us, a record of your consent (when and how you agreed), your opt-in and opt-out history, and the content and delivery status of our messages. We use it only to send the texts you agreed to, reply to your messages, verify your identity when you sign in, keep a record of your choices, meet legal and carrier requirements, and prevent fraud. We keep consent and opt-out records for at least four years after our last message to you.',
					),
				)}
				{section(
					'No sharing.',
					t(
						'No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. We do not sell, rent or share your mobile number or your text messaging opt-in data and consent with anyone, except the service providers and carriers that deliver our messages, which may use it only for that purpose. This does not prevent a disclosure the law requires.',
					),
				)}
				{section(
					'Carriers.',
					t('Mobile carriers are not liable for delayed or undelivered messages.'),
				)}
				{section(
					'Eligibility.',
					t('Our texts are intended for business customers 18 or older in the United States.'),
				)}
				{section(
					'Changes.',
					t('We may update these terms. The date above shows the latest version.'),
				)}

				<Link
					to="/"
					className="inline-block text-sm font-medium text-portal-accent hover:underline"
				>
					{t('Back to home')}
				</Link>
			</div>
		</div>
	);
}
